/**
 * Decides every fetch a delta operation makes: the first, and follow-ups
 * for runs the server reported as incomplete. Each follow-up gets more time;
 * one that makes no progress gets one long attempt, and a failed follow-up
 * is retried once. Each decision also returns the telemetry it implies.
 */

/** Timeout for each attempt, by attempt number. Attempt 0 is the first fetch. */
export const ATTEMPT_TIMEOUTS_SECONDS = [3, 5, 15, 30] as const;
/** The single long attempt after a follow-up makes no progress. */
const ESCALATED_TIMEOUT_SECONDS = 45;
const MAX_FOLLOW_UPS = ATTEMPT_TIMEOUTS_SECONDS.length - 1;

export interface LadderState {
  /** The attempt this state describes. 0 is the first fetch. */
  attempt: number;
  /** This attempt uses the escalated timeout. */
  escalated: boolean;
  /** This attempt retries a failed follow-up. */
  errorRetry: boolean;
  /** Incomplete runs from the latest successful attempt before this one. */
  previousIncompleteRunIds: string[];
  /** This attempt's timeout, in seconds. */
  timeoutSeconds: number;
}

export interface FetchStep {
  kind: 'fetch';
  /**
   * The first fetch replaces the entry's data. A follow-up merges its runs
   * into it.
   */
  mode: 'replace' | 'merge';
  state: LadderState;
}

export type NextStep =
  | FetchStep
  | {
      kind: 'stop';
      reason: 'complete' | 'no_progress' | 'max_retries' | 'failed';
    };

export const FIRST_ATTEMPT: FetchStep = {
  kind: 'fetch',
  mode: 'replace',
  state: {
    attempt: 0,
    errorRetry: false,
    escalated: false,
    previousIncompleteRunIds: [],
    timeoutSeconds: ATTEMPT_TIMEOUTS_SECONDS[0],
  },
};

export type AttemptOutcome =
  | {errorMessage: string; failed: true}
  | {incomplete: string[]};

/** One follow-up telemetry event, without the delta operation's identifiers. */
export interface LadderEvent {
  attempt: number;
  errorMessage?: string;
  incompleteCount: number;
  /** For error_retry: no retry follows. */
  isFinal?: boolean;
  phase:
    | 'initial'
    | 'initial_error'
    | 'retry'
    | 'error_retry'
    | 'no_progress_escalate'
    | 'no_progress_eject'
    | 'max_retries';
  timeoutSeconds?: number;
}

function sameRunIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const ids = new Set(a);
  return b.every(id => ids.has(id));
}

const followUp = (state: LadderState): FetchStep => ({
  kind: 'fetch',
  mode: 'merge',
  state,
});

/** What to do after the attempt `state` describes ended with `outcome`. */
export function nextAttempt(
  state: LadderState,
  outcome: AttemptOutcome
): {events: LadderEvent[]; next: NextStep} {
  const attempt = state.attempt + 1;
  const isFirst = state.attempt === 0;

  if ('failed' in outcome) {
    const {errorMessage} = outcome;
    // A failed first fetch is the request's error; there is nothing to retry
    // a follow-up for.
    if (isFirst) {
      return {
        events: [
          {
            attempt: 0,
            errorMessage,
            incompleteCount: 0,
            phase: 'initial_error',
            timeoutSeconds: state.timeoutSeconds,
          },
        ],
        next: {kind: 'stop', reason: 'failed'},
      };
    }
    let next: NextStep;
    if (state.errorRetry) {
      next = {kind: 'stop', reason: 'failed'};
    } else if (attempt > MAX_FOLLOW_UPS) {
      next = {kind: 'stop', reason: 'max_retries'};
    } else {
      next = followUp({
        ...state,
        attempt,
        errorRetry: true,
        timeoutSeconds: state.escalated
          ? ESCALATED_TIMEOUT_SECONDS
          : ATTEMPT_TIMEOUTS_SECONDS[attempt],
      });
    }
    const events: LadderEvent[] = [
      {
        attempt: state.attempt,
        errorMessage,
        incompleteCount: state.previousIncompleteRunIds.length,
        isFinal: next.kind === 'stop',
        phase: 'error_retry',
      },
    ];
    if (next.kind === 'stop' && next.reason === 'max_retries') {
      events.push({
        attempt: state.attempt,
        incompleteCount: state.previousIncompleteRunIds.length,
        phase: 'max_retries',
        timeoutSeconds: state.timeoutSeconds,
      });
    }
    return {events, next};
  }

  const current = outcome.incomplete;
  const event = (phase: LadderEvent['phase']): LadderEvent => ({
    attempt: state.attempt,
    incompleteCount: current.length,
    phase,
    timeoutSeconds: state.timeoutSeconds,
  });
  const events = [event(isFirst ? 'initial' : 'retry')];
  if (current.length === 0) {
    return {events, next: {kind: 'stop', reason: 'complete'}};
  }
  const noProgress =
    !isFirst && sameRunIds(current, state.previousIncompleteRunIds);
  if (noProgress && state.escalated) {
    events.push(event('no_progress_eject'));
    return {events, next: {kind: 'stop', reason: 'no_progress'}};
  }
  if (attempt > MAX_FOLLOW_UPS) {
    events.push(event('max_retries'));
    return {events, next: {kind: 'stop', reason: 'max_retries'}};
  }
  if (noProgress) {
    events.push(event('no_progress_escalate'));
  }
  return {
    events,
    next: followUp({
      attempt,
      errorRetry: false,
      escalated: noProgress,
      previousIncompleteRunIds: current,
      timeoutSeconds: noProgress
        ? ESCALATED_TIMEOUT_SECONDS
        : ATTEMPT_TIMEOUTS_SECONDS[attempt],
    }),
  };
}
